import { useTranslation } from "react-i18next";

import { VoteStatus } from "../Deliberation/useVoteState";
import ValidateButton from "../common/Buttons/ValidateButton";
import ActionSectionLayout from "components/vote/common/ActionSectionLayout";
import { ERROR_TEXT_COLOR } from "components/common/textColors";
import { VOTEBOX_SUCCESS_COLOR } from "./voteboxTokens";
import { StatusText } from "./ActionSection.styles";
import { RemainingVotes } from "./types";

type Props = {
    voteStatus: VoteStatus;
    remainingWeight?: number;
    // With proxies, the voters whose votes are not all distributed yet.
    remainingVotesByVoter?: Array<RemainingVotes>;
    validateVote: () => void;
    resetVote: () => void;
    canAnswerBeUpdated: boolean;
};

const ActionSection = ({
    voteStatus,
    validateVote,
    resetVote,
    remainingWeight = 0,
    remainingVotesByVoter = [],
    canAnswerBeUpdated,
}: Props) => {
    const { t } = useTranslation();

    // Names the votes left to distribute when they are known, as in the mockup.
    function getIncompleteMessage(): string {
        if (0 < remainingVotesByVoter.length) {
            const voters = remainingVotesByVoter
                .map((voter) =>
                    t("remainingVotesForVoter", {
                        count: voter.remainingWeight,
                        interpolation: { escapeValue: false },
                        name: voter.displayName,
                    }),
                )
                .join(` ${t("and")} `);

            return t("remainingVotesToDistributeFor", {
                interpolation: { escapeValue: false },
                voters,
            });
        }

        if (0 < remainingWeight) {
            return t("remainingVotesToDistribute", { count: remainingWeight });
        }

        return t(`errorVoteIncomplete.${window.env.REACT_APP_THEME}`);
    }

    function renderInformationMessage() {
        switch (voteStatus) {
            case VoteStatus.Incomplete:
                return <StatusText>{getIncompleteMessage()}</StatusText>;
            case VoteStatus.Success:
                return <StatusText>{t("voteRecorded")}</StatusText>;
            case VoteStatus.Loading:
                return <StatusText>...{t("loading")}</StatusText>;
            case VoteStatus.PendingValidation:
                return (
                    <StatusText>
                        {t(`answerNotValidated.${window.env.REACT_APP_THEME}`)}
                    </StatusText>
                );
            case VoteStatus.Error:
                return <StatusText>{t("errorUnknown")}</StatusText>;
        }
    }

    const isSuccess = VoteStatus.Success === voteStatus;
    const shouldDisplayButton = !isSuccess || canAnswerBeUpdated;
    // "Vote incomplet" warns of an error: role="alert" (RGAA 7.5.2)
    const isAlertMessage =
        VoteStatus.Incomplete === voteStatus || VoteStatus.Error === voteStatus;
    const isButtonDisabled =
        VoteStatus.Idle === voteStatus ||
        VoteStatus.Incomplete === voteStatus ||
        VoteStatus.NotAllowed === voteStatus ||
        VoteStatus.Loading === voteStatus ||
        remainingWeight !== 0;
    const handleButtonClick = isSuccess ? resetVote : validateVote;
    const buttonText = isSuccess
        ? t(`changeAnswer.${window.env.REACT_APP_THEME}`)
        : t(`validateAnswer.${window.env.REACT_APP_THEME}`);
    const messageColor = isSuccess ? VOTEBOX_SUCCESS_COLOR : ERROR_TEXT_COLOR;
    const button = shouldDisplayButton ? (
        <ValidateButton
            isDisabled={isButtonDisabled}
            isOutlined={isSuccess}
            onClick={handleButtonClick}
            text={buttonText}
        />
    ) : null;

    return (
        <ActionSectionLayout
            button={button}
            isAlertMessage={isAlertMessage}
            isIdle={VoteStatus.Idle === voteStatus}
            message={renderInformationMessage()}
            messageColor={messageColor}
            messageTestId="message-distribution"
        />
    );
};

export default ActionSection;
